import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { doc, getDoc, setDoc, addDoc, Timestamp, collection, query, where, getDocs } from "firebase/firestore";
import db from "./Firebase";

const compactPhone = (val) => String(val || "").replace(/\D/g, "").slice(-10);

const MEMBERSHIP_TYPES = [
    { type: "shiksha", tag: "shiksha", label: "Shiksha (₹499)", defaultPrice: 499 },
    { type: "shiksha_plus_monthly", tag: "shiksha_plus", label: "Shiksha Plus Monthly (₹199)", defaultPrice: 199 },
    { type: "shiksha_plus_yearly", tag: "shiksha_plus", label: "Shiksha Plus Yearly (₹1999)", defaultPrice: 1999 },
];

const todayStr = () => new Date().toISOString().slice(0, 10);

const toIST = (dateStr) => Timestamp.fromDate(new Date(dateStr + "T00:00:00+05:30"));

// ── Icons ──────────────────────────────────────────────────────────────────
const PersonIcon = ({ size = 16 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" />
    </svg>
);
const PersonPlusIcon = ({ size = 26 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="#3949ab" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" />
        <line x1="19" y1="8" x2="19" y2="14" /><line x1="22" y1="11" x2="16" y2="11" />
    </svg>
);
const PhoneIcon = ({ size = 16 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.61 3.12 2 2 0 0 1 3.6 1h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.69a16 16 0 0 0 6.29 6.29l.95-.95a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
    </svg>
);
const CrownIcon = ({ size = 16 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M2 4l3 12h14l3-12-6 7-4-7-4 7-6-7z" /><line x1="5" y1="20" x2="19" y2="20" />
    </svg>
);
const TagIcon = ({ size = 16 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
        <line x1="7" y1="7" x2="7.01" y2="7" />
    </svg>
);
const CalendarIcon = ({ size = 16 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
        <line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
    </svg>
);
const RupeeIcon = ({ size = 16 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="6" y1="3" x2="18" y2="3" /><line x1="6" y1="8" x2="18" y2="8" />
        <line x1="6" y1="13" x2="12" y2="21" /><line x1="6" y1="13" x2="15" y2="13" />
        <path d="M6 8a5 5 0 0 0 5 5" />
    </svg>
);
const ChevronDown = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="6 9 12 15 18 9" />
    </svg>
);

// ── Shared styles ──────────────────────────────────────────────────────────
const inputStyle = {
    borderRadius: "10px",
    border: "1.5px solid #e5e7eb",
    padding: "11px 12px 11px 40px",
    fontSize: "14px",
    width: "100%",
    outline: "none",
    color: "#374151",
    background: "#fff",
    boxSizing: "border-box",
};
const iconWrap = {
    position: "absolute", left: "13px", top: "50%",
    transform: "translateY(-50%)", color: "#9ca3af",
    display: "flex", pointerEvents: "none",
};
const labelStyle = {
    fontWeight: 700, fontSize: "14px",
    display: "flex", alignItems: "center", gap: "7px",
    marginBottom: "8px", color: "#1f2937",
};

export default function RequestShikshaSubscription() {
    const navigate = useNavigate();

    useEffect(() => {
        const storedEmail = localStorage.getItem("email");
        const storedPassword = localStorage.getItem("password");
        const storedRemember = localStorage.getItem("remember");
        if (storedRemember === "false" || !storedEmail || !storedPassword) {
            navigate("/signin");
        }
    }, [navigate]);

    const adminEmail = localStorage.getItem("email") || "unknown";

    const [form, setForm] = useState({
        name: "",
        phone: "",
        membershipType: "shiksha",
        tag: "shiksha",
        price: "499",
        startDate: todayStr(),
        endDate: "",
    });
    const [submitStatus, setSubmitStatus] = useState(null);
    const [errorMsg, setErrorMsg] = useState("");
    const [existingData, setExistingData] = useState(null);
    const [nameAutoFilled, setNameAutoFilled] = useState(false);
    const [nameFetching, setNameFetching] = useState(false);

    const handleMembershipChange = (e) => {
        const selected = MEMBERSHIP_TYPES.find(m => m.type === e.target.value);
        if (selected) {
            setForm(f => ({ ...f, membershipType: selected.type, tag: selected.tag, price: String(selected.defaultPrice) }));
        }
    };

    const handlePhoneChange = async (e) => {
        const digits = e.target.value.replace(/\D/g, "").slice(0, 10);
        setForm(f => ({ ...f, phone: digits, name: digits.length < 10 ? "" : f.name }));
        setExistingData(null);
        if (digits.length < 10) { setNameAutoFilled(false); return; }

        // Exactly 10 digits — fetch personal_details + shiksha subscription in parallel
        setNameFetching(true);
        try {
            const [pdSnap, subSnap] = await Promise.all([
                getDocs(query(collection(db, "personal_details"), where("phone_no", "==", digits))),
                getDoc(doc(db, "nv_shiksha_subscriptions", digits)),
            ]);

            // Auto-fill name from personal_details
            if (!pdSnap.empty) {
                const fullName = pdSnap.docs[0].data().full_name || "";
                setForm(f => ({ ...f, name: fullName }));
                setNameAutoFilled(true);
            } else {
                setNameAutoFilled(false);
            }

            // Existing shiksha subscription warning
            if (subSnap.exists()) {
                const d = subSnap.data();
                setExistingData({
                    name: d.name || "-",
                    membershipType: d.membership_type || "-",
                    logCount: Array.isArray(d.subscription_log) ? d.subscription_log.length : 0,
                });
            }
        } catch (_) {
            setNameAutoFilled(false);
        } finally {
            setNameFetching(false);
        }
    };

    const handleSubmit = async () => {
        const phone = compactPhone(form.phone);
        if (phone.length !== 10) { setErrorMsg("Valid 10-digit phone number required."); return; }
        if (!form.name.trim()) { setErrorMsg("Name is required."); return; }
        if (!form.startDate) { setErrorMsg("Start date is required."); return; }
        if (!form.endDate) { setErrorMsg("End date is required."); return; }

        setSubmitStatus("loading");
        setErrorMsg("");
        try {
            const nowTs = Timestamp.now();
            const startTs = toIST(form.startDate);
            const endTs = toIST(form.endDate);
            const price = parseInt(form.price) || 0;

            const logEntry = { created_date: nowTs, membership_type: form.membershipType, price };

            const docRef = doc(db, "nv_shiksha_subscriptions", phone);
            const docSnap = await getDoc(docRef);
            const isNew = !docSnap.exists();
            const existingLog = isNew ? [] : (Array.isArray(docSnap.data().subscription_log) ? docSnap.data().subscription_log : []);

            await setDoc(docRef, {
                created_date: isNew ? nowTs : (docSnap.data().created_date || nowTs),
                name: form.name.trim(),
                phone_no: phone,
                membership_type: form.membershipType,
                price,
                tag: form.tag,
                start_date: startTs,
                end_date: endTs,
                status: "active",
                subscription_log: [...existingLog, logEntry],
            });

            await addDoc(collection(db, "nv_manual_membership_allocation"), {
                action: isNew ? "created" : "updated",
                phone_no: phone,
                name: form.name.trim(),
                membership_type: form.membershipType,
                price,
                tag: form.tag,
                start_date: startTs,
                end_date: endTs,
                allocated_by: adminEmail,
                created_at: nowTs,
            });

            setSubmitStatus(isNew ? "created" : "updated");
            setExistingData(null);
        } catch (err) {
            console.error("RequestShikshaSubscription error:", err);
            setErrorMsg(err.message || "Something went wrong.");
            setSubmitStatus("error");
        }
    };

    const handleReset = () => {
        setForm({ name: "", phone: "", membershipType: "shiksha", tag: "shiksha", price: "499", startDate: todayStr(), endDate: "" });
        setSubmitStatus(null);
        setErrorMsg("");
        setExistingData(null);
        setNameAutoFilled(false);
        setNameFetching(false);
    };

    return (
        <div style={{
            padding: "40px 20px",
            display: "flex",
            justifyContent: "center",
            alignItems: "flex-start",
        }}>
            <div style={{ width: "100%", maxWidth: "860px" }}>

                {/* HEADER */}
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <div className="d-flex align-items-center gap-3">
                        <div style={{
                            width: "52px", height: "52px", borderRadius: "50%",
                            background: "#c5cae9", display: "flex",
                            alignItems: "center", justifyContent: "center", flexShrink: 0,
                        }}>
                            <PersonPlusIcon />
                        </div>
                        <div>
                            <div style={{ fontSize: "22px", fontWeight: 800, color: "#1a1a2e", lineHeight: 1.2 }}>
                                Add Shiksha Subscription
                            </div>
                            {/* <div style={{ fontSize: "13px", color: "#6b7280", marginTop: "3px" }}>
                                Create or update record in{" "}
                                <span style={{
                                    background: "#ede7f6", color: "#5e35b1",
                                    borderRadius: "6px", padding: "2px 10px",
                                    fontSize: "12px", fontWeight: 600, fontFamily: "monospace",
                                }}>
                                    nv_shiksha_subscriptions
                                </span>
                            </div> */}
                        </div>
                    </div>
                    <Link to="/" style={{
                        border: "1.5px solid #d1d5db", borderRadius: "20px",
                        padding: "6px 18px", fontSize: "14px", color: "#374151",
                        textDecoration: "none", display: "flex", alignItems: "center", gap: "5px",
                        background: "#fff", fontWeight: 500, whiteSpace: "nowrap",
                    }}>
                        ← Back
                    </Link>
                </div>

                {/* CARD */}
                <div style={{
                    background: "#f5f6f8", borderRadius: "20px",
                    padding: "36px 32px",
                    boxShadow: "0 4px 32px rgba(94,53,177,0.08)",
                }}>

                    {/* ROW 1 — PHONE + NAME */}
                    <div className="row g-3 mb-4">
                        <div className="col-md-6">
                            <div style={labelStyle}>
                                <PhoneIcon /> Phone No <span style={{ color: "#e53e3e" }}>*</span>
                            </div>
                            <div style={{ position: "relative" }}>
                                {/* <span style={iconWrap}><PhoneIcon /></span> */}
                                <input
                                    type="tel"
                                    style={inputStyle}
                                    placeholder="10-digit phone number"
                                    value={form.phone}
                                    maxLength={10}
                                    onChange={handlePhoneChange}
                                />
                                {nameFetching && (
                                    <span style={{ position: "absolute", right: "13px", top: "50%", transform: "translateY(-50%)" }}>
                                        <span className="spinner-border spinner-border-sm text-secondary" style={{ width: "14px", height: "14px" }} />
                                    </span>
                                )}
                            </div>
                            {existingData && (
                                <div style={{
                                    background: "#fffbeb", border: "1px solid #f59e0b",
                                    borderRadius: "8px", padding: "10px 14px", marginTop: "8px",
                                    fontSize: "12px", color: "#92400e",
                                }}>
                                    <strong>⚠ Existing record found</strong> — Name: <strong>{existingData.name}</strong> ·
                                    Plan: <strong>{existingData.membershipType}</strong> ·
                                    Log entries: <strong>{existingData.logCount}</strong>.{" "}
                                    Submitting will <u>update</u> this record and append a new log entry.
                                </div>
                            )}
                        </div>
                        <div className="col-md-6">
                            <div style={labelStyle}>
                                <PersonIcon /> Full Name <span style={{ color: "#e53e3e" }}>*</span>
                            </div>
                            <div style={{ position: "relative" }}>
                                {/* <span style={iconWrap}><PersonIcon /></span> */}
                                <input
                                    type="text"
                                    style={nameAutoFilled
                                        ? { ...inputStyle, background: "#f0fdf4", color: "#15803d", cursor: "not-allowed" }
                                        : inputStyle
                                    }
                                    placeholder="Auto-filled from phone number"
                                    value={form.name}
                                    readOnly={nameAutoFilled}
                                    onChange={nameAutoFilled ? undefined : e => setForm(f => ({ ...f, name: e.target.value }))}
                                />
                                {nameAutoFilled && (
                                    <span style={{ position: "absolute", right: "13px", top: "50%", transform: "translateY(-50%)", fontSize: "11px", color: "#15803d", fontWeight: 600 }}>
                                        ✓ Auto
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* ROW 2 — MEMBERSHIP + PRICE + TAG */}
                    <div className="row g-3 mb-4">
                        <div className="col-md-4">
                            <div style={labelStyle}>
                                Membership Type <span style={{ color: "#e53e3e" }}>*</span>
                            </div>
                            <div style={{ position: "relative" }}>
                                {/* <span style={iconWrap}><CrownIcon /></span> */}
                                <select
                                    style={{ ...inputStyle, appearance: "none", cursor: "pointer" }}
                                    value={form.membershipType}
                                    onChange={handleMembershipChange}
                                >
                                    {MEMBERSHIP_TYPES.map(m => (
                                        <option key={m.type} value={m.type}>{m.label}</option>
                                    ))}
                                </select>
                                <span style={{ position: "absolute", right: "13px", top: "50%", transform: "translateY(-50%)", color: "#9ca3af", pointerEvents: "none" }}>
                                    <ChevronDown />
                                </span>
                            </div>
                        </div>
                        <div className="col-md-4">
                            <div style={labelStyle}>
                                Price (₹) <span style={{ color: "#e53e3e" }}>*</span>
                            </div>
                            <div style={{ position: "relative" }}>
                                {/* <span style={iconWrap}><RupeeIcon /></span> */}
                                <input
                                    type="number"
                                    style={inputStyle}
                                    value={form.price}
                                    min="0"
                                    onChange={e => setForm(f => ({ ...f, price: e.target.value }))}
                                />
                            </div>
                        </div>
                        <div className="col-md-4">
                            <div style={labelStyle}>
                                Tag
                            </div>
                            <div style={{ position: "relative" }}>
                                {/* <span style={iconWrap}><TagIcon /></span> */}
                                <input
                                    type="text"
                                    style={{ ...inputStyle, background: "#f3f4f6", color: "#6b7280", cursor: "not-allowed" }}
                                    value={form.tag}
                                    readOnly
                                />
                            </div>
                        </div>
                    </div>

                    {/* START + END DATE */}
                    <div className="row g-3 mb-4">
                        <div className="col-6">
                            <div style={labelStyle}>
                                <CalendarIcon /> Start Date <span style={{ color: "#e53e3e" }}>*</span>
                            </div>
                            <div style={{ position: "relative" }}>
                                {/* <span style={iconWrap}><CalendarIcon /></span> */}
                                <input
                                    type="date"
                                    style={inputStyle}
                                    value={form.startDate}
                                    onChange={e => setForm(f => ({ ...f, startDate: e.target.value }))}
                                />
                            </div>
                        </div>
                        <div className="col-6">
                            <div style={labelStyle}>
                                <CalendarIcon /> End Date <span style={{ color: "#e53e3e" }}>*</span>
                            </div>
                            <div style={{ position: "relative" }}>
                                {/* <span style={iconWrap}><CalendarIcon /></span> */}
                                <input
                                    type="date"
                                    style={inputStyle}
                                    value={form.endDate}
                                    onChange={e => setForm(f => ({ ...f, endDate: e.target.value }))}
                                />
                            </div>
                        </div>
                    </div>

                    {/* PREVIEW */}
                    {form.phone && form.name && form.endDate && (
                        <div style={{
                            background: "#f8fafc", border: "1px solid #e2e8f0",
                            borderRadius: "10px", padding: "14px 16px",
                            marginBottom: "20px", fontSize: "12px",
                        }}>
                            <div style={{ fontWeight: 700, fontSize: "13px", marginBottom: "6px" }}>Preview</div>
                            <div><span style={{ color: "#6b7280" }}>Document ID:</span> <strong>{compactPhone(form.phone) || "-"}</strong></div>
                            <div><span style={{ color: "#6b7280" }}>Name:</span> <strong>{form.name}</strong></div>
                            <div><span style={{ color: "#6b7280" }}>Membership:</span> <strong>{form.membershipType}</strong> · Tag: <strong>{form.tag}</strong> · ₹<strong>{form.price}</strong></div>
                            <div><span style={{ color: "#6b7280" }}>Period:</span> <strong>{form.startDate}</strong> → <strong>{form.endDate}</strong></div>
                            <div><span style={{ color: "#6b7280" }}>Status:</span> <strong>active</strong></div>
                        </div>
                    )}

                    {/* ERROR */}
                    {errorMsg && (
                        <div style={{
                            background: "#fef2f2", border: "1px solid #fca5a5",
                            borderRadius: "8px", padding: "10px 14px",
                            marginBottom: "16px", fontSize: "13px", color: "#b91c1c",
                        }}>
                            {errorMsg}
                        </div>
                    )}

                    {/* SUCCESS */}
                    {(submitStatus === "created" || submitStatus === "updated") && (
                        <div style={{
                            background: "#f0fdf4", border: "1px solid #86efac",
                            borderRadius: "8px", padding: "10px 14px",
                            marginBottom: "16px", fontSize: "13px", color: "#15803d",
                        }}>
                            {submitStatus === "created"
                                ? "✓ New subscription created successfully!"
                                : "✓ Subscription updated & new log entry appended!"}
                        </div>
                    )}

                    {/* SUBMIT BUTTON */}
                    <button
                        style={{
                            width: "100%", padding: "14px",
                            background: submitStatus === "loading" ? "#818cf8" : "#4f46e5",
                            color: "#fff", border: "none", borderRadius: "12px",
                            fontSize: "15px", fontWeight: 700, cursor: submitStatus === "loading" ? "not-allowed" : "pointer",
                            display: "flex", alignItems: "center", justifyContent: "center", gap: "8px",
                            transition: "background 0.2s",
                        }}
                        disabled={submitStatus === "loading"}
                        onClick={handleSubmit}
                    >
                        {submitStatus === "loading" ? (
                            <><span className="spinner-border spinner-border-sm" style={{ width: "16px", height: "16px" }} /> Saving…</>
                        ) : existingData ? (
                            "↑ Update Subscription"
                        ) : (
                            "+ Create Subscription"
                        )}
                    </button>

                    {(submitStatus === "created" || submitStatus === "updated") && (
                        <button
                            style={{
                                width: "100%", padding: "12px", marginTop: "10px",
                                background: "transparent", color: "#4f46e5",
                                border: "1.5px solid #4f46e5", borderRadius: "12px",
                                fontSize: "14px", fontWeight: 600, cursor: "pointer",
                            }}
                            onClick={handleReset}
                        >
                            Add Another
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}


// import React, { useState, useEffect } from "react";
// import { Link, useNavigate } from "react-router-dom";
// import { addDoc, Timestamp, collection, query, where, getDocs, getDoc, doc } from "firebase/firestore";
// import db from "./Firebase";

// const compactPhone = (val) => String(val || "").replace(/\D/g, "").slice(-10);

// const MEMBERSHIP_TYPES = [
//     { type: "shiksha", tag: "shiksha", label: "Shiksha (₹499)", defaultPrice: 499 },
//     { type: "shiksha_plus_monthly", tag: "shiksha_plus", label: "Shiksha Plus Monthly (₹199)", defaultPrice: 199 },
//     { type: "shiksha_plus_yearly", tag: "shiksha_plus", label: "Shiksha Plus Yearly (₹1999)", defaultPrice: 1999 },
// ];

// const todayStr = () => new Date().toISOString().slice(0, 10);
// const toIST = (dateStr) => Timestamp.fromDate(new Date(dateStr + "T00:00:00+05:30"));

// const PersonIcon = ({ size = 16 }) => (
//     <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
//         <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" />
//     </svg>
// );
// const PersonPlusIcon = ({ size = 26 }) => (
//     <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="#3949ab" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
//         <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" />
//         <line x1="19" y1="8" x2="19" y2="14" /><line x1="22" y1="11" x2="16" y2="11" />
//     </svg>
// );
// const PhoneIcon = ({ size = 16 }) => (
//     <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
//         <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.61 3.12 2 2 0 0 1 3.6 1h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.69a16 16 0 0 0 6.29 6.29l.95-.95a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
//     </svg>
// );
// const CalendarIcon = ({ size = 16 }) => (
//     <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
//         <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
//         <line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
//     </svg>
// );
// const ChevronDown = () => (
//     <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
//         <polyline points="6 9 12 15 18 9" />
//     </svg>
// );

// const inputStyle = {
//     borderRadius: "10px",
//     border: "1.5px solid #e5e7eb",
//     padding: "11px 12px",
//     fontSize: "14px",
//     width: "100%",
//     outline: "none",
//     color: "#374151",
//     background: "#fff",
//     boxSizing: "border-box",
// };
// const labelStyle = {
//     fontWeight: 700, fontSize: "14px",
//     display: "flex", alignItems: "center", gap: "7px",
//     marginBottom: "8px", color: "#1f2937",
// };

// export default function RequestShikshaSubscription() {
//     const navigate = useNavigate();

//     useEffect(() => {
//         const storedEmail = localStorage.getItem("email");
//         const storedPassword = localStorage.getItem("password");
//         const storedRemember = localStorage.getItem("remember");
//         if (storedRemember === "false" || !storedEmail || !storedPassword) {
//             navigate("/signin");
//         }
//     }, [navigate]);

//     const requestedBy = localStorage.getItem("email") || "unknown";

//     const [form, setForm] = useState({
//         name: "",
//         phone: "",
//         membershipType: "shiksha",
//         tag: "shiksha",
//         price: "499",
//         startDate: todayStr(),
//         endDate: "",
//     });
//     const [submitStatus, setSubmitStatus] = useState(null);
//     const [errorMsg, setErrorMsg] = useState("");
//     const [existingData, setExistingData] = useState(null);
//     const [nameAutoFilled, setNameAutoFilled] = useState(false);
//     const [nameFetching, setNameFetching] = useState(false);
//     const [showToast, setShowToast] = useState(false);

//     const handleMembershipChange = (e) => {
//         const selected = MEMBERSHIP_TYPES.find(m => m.type === e.target.value);
//         if (selected) {
//             setForm(f => ({ ...f, membershipType: selected.type, tag: selected.tag, price: String(selected.defaultPrice) }));
//         }
//     };

//     const handlePhoneChange = async (e) => {
//         const digits = e.target.value.replace(/\D/g, "").slice(0, 10);
//         setForm(f => ({ ...f, phone: digits, name: digits.length < 10 ? "" : f.name }));
//         setExistingData(null);
//         if (digits.length < 10) { setNameAutoFilled(false); return; }

//         setNameFetching(true);
//         try {
//             const [pdSnap, subSnap] = await Promise.all([
//                 getDocs(query(collection(db, "personal_details"), where("phone_no", "==", digits))),
//                 getDoc(doc(db, "nv_shiksha_subscriptions", digits)),
//             ]);
//             if (!pdSnap.empty) {
//                 const fullName = pdSnap.docs[0].data().full_name || "";
//                 setForm(f => ({ ...f, name: fullName }));
//                 setNameAutoFilled(true);
//             } else {
//                 setNameAutoFilled(false);
//             }
//             if (subSnap.exists()) {
//                 const d = subSnap.data();
//                 setExistingData({
//                     name: d.name || "-",
//                     membershipType: d.membership_type || "-",
//                     logCount: Array.isArray(d.subscription_log) ? d.subscription_log.length : 0,
//                 });
//             }
//         } catch (_) {
//             setNameAutoFilled(false);
//         } finally {
//             setNameFetching(false);
//         }
//     };

//     const triggerToast = () => {
//         setShowToast(true);
//         setTimeout(() => setShowToast(false), 4000);
//     };

//     const handleSubmit = async () => {
//         const phone = compactPhone(form.phone);
//         if (phone.length !== 10) { setErrorMsg("Valid 10-digit phone number required."); return; }
//         if (!form.name.trim()) { setErrorMsg("Name is required."); return; }
//         if (!form.startDate) { setErrorMsg("Start date is required."); return; }
//         if (!form.endDate) { setErrorMsg("End date is required."); return; }

//         setSubmitStatus("loading");
//         setErrorMsg("");
//         try {
//             await addDoc(collection(db, "nv_subscription_requests"), {
//                 phone_no: phone,
//                 name: form.name.trim(),
//                 membership_type: form.membershipType,
//                 price: parseInt(form.price) || 0,
//                 tag: form.tag,
//                 start_date: toIST(form.startDate),
//                 end_date: toIST(form.endDate),
//                 requested_by: requestedBy,
//                 status: "pending",
//                 created_at: Timestamp.now(),
//             });

//             setSubmitStatus("success");
//             triggerToast();
//             setExistingData(null);
//         } catch (err) {
//             console.error("RequestShikshaSubscription error:", err);
//             setErrorMsg(err.message || "Something went wrong.");
//             setSubmitStatus("error");
//         }
//     };

//     const handleReset = () => {
//         setForm({ name: "", phone: "", membershipType: "shiksha", tag: "shiksha", price: "499", startDate: todayStr(), endDate: "" });
//         setSubmitStatus(null);
//         setErrorMsg("");
//         setExistingData(null);
//         setNameAutoFilled(false);
//         setNameFetching(false);
//     };

//     return (
//         <div style={{ padding: "40px 20px", display: "flex", justifyContent: "center", alignItems: "flex-start" }}>

//             {/* TOAST */}
//             {showToast && (
//                 <div style={{
//                     position: "fixed", top: "24px", right: "24px", zIndex: 99999,
//                     background: "#052e16", color: "#bbf7d0",
//                     borderRadius: "12px", padding: "14px 22px",
//                     fontSize: "14px", fontWeight: 600,
//                     boxShadow: "0 8px 32px rgba(0,0,0,0.25)",
//                     display: "flex", alignItems: "center", gap: "10px",
//                     animation: "fadeInDown 0.3s ease",
//                 }}>
//                     <span style={{ fontSize: "20px" }}>✓</span>
//                     Request sent to admin successfully! We'll process it soon.
//                 </div>
//             )}

//             <div style={{ width: "100%", maxWidth: "860px" }}>

//                 {/* HEADER */}
//                 <div className="d-flex justify-content-between align-items-center mb-4">
//                     <div className="d-flex align-items-center gap-3">
//                         <div style={{
//                             width: "52px", height: "52px", borderRadius: "50%",
//                             background: "#c5cae9", display: "flex",
//                             alignItems: "center", justifyContent: "center", flexShrink: 0,
//                         }}>
//                             <PersonPlusIcon />
//                         </div>
//                         <div>
//                             <div style={{ fontSize: "22px", fontWeight: 800, color: "#1a1a2e", lineHeight: 1.2 }}>
//                                 Request Shiksha Subscription
//                             </div>
//                             {/* <div style={{ fontSize: "12px", color: "#6b7280", marginTop: "3px" }}>
//                                 Requesting as: <strong style={{ color: "#374151" }}>{requestedBy}</strong>
//                             </div> */}
//                         </div>
//                     </div>
//                     <Link to="/" style={{
//                         border: "1.5px solid #d1d5db", borderRadius: "20px",
//                         padding: "6px 18px", fontSize: "14px", color: "#374151",
//                         textDecoration: "none", display: "flex", alignItems: "center", gap: "5px",
//                         background: "#fff", fontWeight: 500, whiteSpace: "nowrap",
//                     }}>
//                         ← Back
//                     </Link>
//                 </div>

//                 {/* CARD */}
//                 <div style={{
//                     background: "#f5f6f8", borderRadius: "20px",
//                     padding: "36px 32px",
//                     boxShadow: "0 4px 32px rgba(94,53,177,0.08)",
//                 }}>

//                     {/* ROW 1 — PHONE + NAME */}
//                     <div className="row g-3 mb-4">
//                         <div className="col-md-6">
//                             <div style={labelStyle}>
//                                 <PhoneIcon /> Phone No <span style={{ color: "#e53e3e" }}>*</span>
//                             </div>
//                             <div style={{ position: "relative" }}>
//                                 <input
//                                     type="tel"
//                                     style={inputStyle}
//                                     placeholder="10-digit phone number"
//                                     value={form.phone}
//                                     maxLength={10}
//                                     onChange={handlePhoneChange}
//                                 />
//                                 {nameFetching && (
//                                     <span style={{ position: "absolute", right: "13px", top: "50%", transform: "translateY(-50%)" }}>
//                                         <span className="spinner-border spinner-border-sm text-secondary" style={{ width: "14px", height: "14px" }} />
//                                     </span>
//                                 )}
//                             </div>
//                             {existingData && (
//                                 <div style={{
//                                     background: "#fffbeb", border: "1px solid #f59e0b",
//                                     borderRadius: "8px", padding: "10px 14px", marginTop: "8px",
//                                     fontSize: "12px", color: "#92400e",
//                                 }}>
//                                     <strong>⚠ Existing subscription found</strong> — Name: <strong>{existingData.name}</strong> ·
//                                     Plan: <strong>{existingData.membershipType}</strong>
//                                 </div>
//                             )}
//                         </div>
//                         <div className="col-md-6">
//                             <div style={labelStyle}>
//                                 <PersonIcon /> Full Name <span style={{ color: "#e53e3e" }}>*</span>
//                             </div>
//                             <div style={{ position: "relative" }}>
//                                 <input
//                                     type="text"
//                                     style={nameAutoFilled
//                                         ? { ...inputStyle, background: "#f0fdf4", color: "#15803d", cursor: "not-allowed" }
//                                         : inputStyle
//                                     }
//                                     placeholder="Auto-filled from phone number"
//                                     value={form.name}
//                                     readOnly={nameAutoFilled}
//                                     onChange={nameAutoFilled ? undefined : e => setForm(f => ({ ...f, name: e.target.value }))}
//                                 />
//                                 {nameAutoFilled && (
//                                     <span style={{ position: "absolute", right: "13px", top: "50%", transform: "translateY(-50%)", fontSize: "11px", color: "#15803d", fontWeight: 600 }}>
//                                         ✓ Auto
//                                     </span>
//                                 )}
//                             </div>
//                         </div>
//                     </div>

//                     {/* ROW 2 — MEMBERSHIP + PRICE + TAG */}
//                     <div className="row g-3 mb-4">
//                         <div className="col-md-4">
//                             <div style={labelStyle}>
//                                 Membership Type <span style={{ color: "#e53e3e" }}>*</span>
//                             </div>
//                             <div style={{ position: "relative" }}>
//                                 <select
//                                     style={{ ...inputStyle, appearance: "none", cursor: "pointer" }}
//                                     value={form.membershipType}
//                                     onChange={handleMembershipChange}
//                                 >
//                                     {MEMBERSHIP_TYPES.map(m => (
//                                         <option key={m.type} value={m.type}>{m.label}</option>
//                                     ))}
//                                 </select>
//                                 <span style={{ position: "absolute", right: "13px", top: "50%", transform: "translateY(-50%)", color: "#9ca3af", pointerEvents: "none" }}>
//                                     <ChevronDown />
//                                 </span>
//                             </div>
//                         </div>
//                         <div className="col-md-4">
//                             <div style={labelStyle}>
//                                 Price (₹) <span style={{ color: "#e53e3e" }}>*</span>
//                             </div>
//                             <input
//                                 type="number"
//                                 style={inputStyle}
//                                 value={form.price}
//                                 min="0"
//                                 onChange={e => setForm(f => ({ ...f, price: e.target.value }))}
//                             />
//                         </div>
//                         <div className="col-md-4">
//                             <div style={labelStyle}>Tag</div>
//                             <input
//                                 type="text"
//                                 style={{ ...inputStyle, background: "#f3f4f6", color: "#6b7280", cursor: "not-allowed" }}
//                                 value={form.tag}
//                                 readOnly
//                             />
//                         </div>
//                     </div>

//                     {/* START + END DATE */}
//                     <div className="row g-3 mb-4">
//                         <div className="col-6">
//                             <div style={labelStyle}>
//                                 <CalendarIcon /> Start Date <span style={{ color: "#e53e3e" }}>*</span>
//                             </div>
//                             <input
//                                 type="date"
//                                 style={inputStyle}
//                                 value={form.startDate}
//                                 onChange={e => setForm(f => ({ ...f, startDate: e.target.value }))}
//                             />
//                         </div>
//                         <div className="col-6">
//                             <div style={labelStyle}>
//                                 <CalendarIcon /> End Date <span style={{ color: "#e53e3e" }}>*</span>
//                             </div>
//                             <input
//                                 type="date"
//                                 style={inputStyle}
//                                 value={form.endDate}
//                                 onChange={e => setForm(f => ({ ...f, endDate: e.target.value }))}
//                             />
//                         </div>
//                     </div>

//                     {/* PREVIEW */}
//                     {form.phone && form.name && form.endDate && (
//                         <div style={{
//                             background: "#f8fafc", border: "1px solid #e2e8f0",
//                             borderRadius: "10px", padding: "14px 16px",
//                             marginBottom: "20px", fontSize: "12px",
//                         }}>
//                             <div style={{ fontWeight: 700, fontSize: "13px", marginBottom: "6px" }}>Preview</div>
//                             <div><span style={{ color: "#6b7280" }}>Phone:</span> <strong>{compactPhone(form.phone)}</strong></div>
//                             <div><span style={{ color: "#6b7280" }}>Name:</span> <strong>{form.name}</strong></div>
//                             <div><span style={{ color: "#6b7280" }}>Membership:</span> <strong>{form.membershipType}</strong> · Tag: <strong>{form.tag}</strong> · ₹<strong>{form.price}</strong></div>
//                             <div><span style={{ color: "#6b7280" }}>Period:</span> <strong>{form.startDate}</strong> → <strong>{form.endDate}</strong></div>
//                         </div>
//                     )}

//                     {/* ERROR */}
//                     {errorMsg && (
//                         <div style={{
//                             background: "#fef2f2", border: "1px solid #fca5a5",
//                             borderRadius: "8px", padding: "10px 14px",
//                             marginBottom: "16px", fontSize: "13px", color: "#b91c1c",
//                         }}>
//                             {errorMsg}
//                         </div>
//                     )}

//                     {/* SUBMIT */}
//                     <button
//                         style={{
//                             width: "100%", padding: "14px",
//                             background: submitStatus === "loading" ? "#818cf8" : "#4f46e5",
//                             color: "#fff", border: "none", borderRadius: "12px",
//                             fontSize: "15px", fontWeight: 700,
//                             cursor: submitStatus === "loading" ? "not-allowed" : "pointer",
//                             display: "flex", alignItems: "center", justifyContent: "center", gap: "8px",
//                             transition: "background 0.2s",
//                         }}
//                         disabled={submitStatus === "loading"}
//                         onClick={handleSubmit}
//                     >
//                         {submitStatus === "loading" ? (
//                             <><span className="spinner-border spinner-border-sm" style={{ width: "16px", height: "16px" }} /> Sending Request…</>
//                         ) : (
//                             "📨 Send Subscription Request"
//                         )}
//                     </button>

//                     {submitStatus === "success" && (
//                         <button
//                             style={{
//                                 width: "100%", padding: "12px", marginTop: "10px",
//                                 background: "transparent", color: "#4f46e5",
//                                 border: "1.5px solid #4f46e5", borderRadius: "12px",
//                                 fontSize: "14px", fontWeight: 600, cursor: "pointer",
//                             }}
//                             onClick={handleReset}
//                         >
//                             Add Another Request
//                         </button>
//                     )}
//                 </div>
//             </div>
//         </div>
//     );
// }
